import { CounterCustomerChargeSearchCriteria } from "domain/entity/CounterCustomerCharge/CounterCustomerChargeSearchCriteria";
import { getDefaultBuConfig, getDefaultBuConfigElement } from "domain/repository/apiUtil";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { CommonMessageModal } from "presentation/view/components/CommonMessageModal";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { useCallback, useEffect, useState } from "react";
import { HPHButton, IconButton } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";
import CounterCustomerChargeSearchForm from "./CounterCustomerChargeSearchForm";

const CounterCustomerChargeSearchPanel = () => {
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant;
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const messageBarVM = useMessageBarVM();
    const { returnCntrNo, returnBlNo, searchCriteria, isShowSerachClose } = counterCustomerChargeState;
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const [isShowMultipleBlMsg, setIsShowMultipleBlMsg] = useState<boolean>(false);
    const [isShowSingBlMsg, setIsShowSingBlMsg] = useState<boolean>(false);
    const [singleMultipleValidationMap, setSingleMultipleValidationMap] = useState<{ [key: string]: object }>({});
    const railEnable: boolean = getDefaultBuConfigElement<boolean>("REACT_APP_FIELD_ENABLE", "RAIL") || false;
    const isRailMaster: boolean = getDefaultBuConfig<boolean>('REACT_APP_RAIL_ENABLE') || false;

    useEffect(() => {
        if (singleMultipleValidationMap) {
            let isSingleOrMultipleConsignee: string = "";
            isSingleOrMultipleConsignee = singleMultipleValidationMap["SingleOrMultiple"] as unknown as string;
            //search by cntr# and BL#
            if (isSingleOrMultipleConsignee === "Single") {
                counterCustomerChargeVM.getSingOrMultipleBlInfo(singleMultipleValidationMap);
                setIsShowSingBlMsg(true);
            }
            //copy by cntr# and consignee
            if (isSingleOrMultipleConsignee === "Multiple") {
                counterCustomerChargeVM.getSingOrMultipleBlInfo(singleMultipleValidationMap);
                setIsShowMultipleBlMsg(true);
            }
        }
    }, [counterCustomerChargeVM, singleMultipleValidationMap]);

    const resetButtonClicked = useCallback(() => {
        counterCustomerChargeVM.onReset();
    }, [counterCustomerChargeVM]);

    const searchButtonClicked = useCallback(async (criteria: CounterCustomerChargeSearchCriteria) => {
        if (!criteria.billOfLadingNo && !criteria.shippingBill && !criteria.serviceRequestRef && (!criteria.cntrList || criteria.cntrList.length === 0 || (criteria.cntrList.length === 1 && criteria.cntrList[0] === ''))) {
            if (isRailMaster && railEnable) {
                if (!criteria.trainId) {
                    messageBarVM.showError("Container No., Bill of Lading / Ref. No., Shipping Bill, Service Request Ref., Train ID must input one of them at least.");
                    return;
                }
            } else {
                messageBarVM.showError("Container No., Bill of Lading / Ref. No., Shipping Bill, Service Request Ref. must input one of them at least.");
                return;
            }

        }

        const cntrNos = criteria.cntrList;
        //Check if cnrt no is duplicated
        if (cntrNos && cntrNos?.length > 0) {
            const hasDuplicates = () => {
                const cleaned = (cntrNos || [])
                    .map(cntrNo => typeof cntrNo === 'string' ? cntrNo.trim() : cntrNo)
                    .filter(Boolean);
                return new Set(cleaned).size !== cleaned.length;
            };

            if (hasDuplicates()) {
                messageBarVM.showError('The Container No. contain duplicates. Please check your input.');
                return;
            }
        }

        if ((criteria.billOfLadingNo && criteria.billOfLadingNo.includes('*')) || (criteria.shippingBill && criteria.shippingBill.includes('*'))) {
            messageBarVM.showError("BL# and SB# not allow input '*'.");
            return;
        }
        // await counterCustomerChargeVM.onSearchClick();
        counterCustomerChargeVM.onShowLoading();
        await counterCustomerChargeVM.onSearch(criteria, operatingUnit).then(async (data) => {
            if (criteria.billOfLadingNo) {
                await counterCustomerChargeVM.checkChargeDataForMultipleBL(criteria).then((data) => {
                    if (data && data !== undefined) {
                        setSingleMultipleValidationMap(data);
                        counterCustomerChargeVM.onHideLoading();
                    } else {
                        setSingleMultipleValidationMap({});
                        counterCustomerChargeVM.onHideLoading();
                    }
                });
            } else {
                counterCustomerChargeVM.onHideLoading();
            }
            if (data) {
                messageBarVM.showError(data);
            }
        }).catch((error) => {
            counterCustomerChargeVM.onHideLoading();
        });
    }, [counterCustomerChargeVM, messageBarVM, operatingUnit, railEnable, isRailMaster])

    const handleSingBlMsg = useCallback(async () => {
        setIsShowSingBlMsg(false);
        counterCustomerChargeVM.onShowLoading();
        const newSearchCriteira: CounterCustomerChargeSearchCriteria = {
            ...searchCriteria,
            cntrList: [...returnCntrNo],
            billOfLadingNo: returnBlNo,
        }
        await counterCustomerChargeVM.handleSingBlForSearch(newSearchCriteira).then((data) => {
            counterCustomerChargeVM.onHideLoading();
        });

    }, [counterCustomerChargeVM, returnBlNo, returnCntrNo, searchCriteria]);

    const handleMultipleBlMsg = useCallback(async () => {
        setIsShowMultipleBlMsg(false);
        counterCustomerChargeVM.onShowLoading();
        await counterCustomerChargeVM.handleMultilpeBlForCopyCharges(searchCriteria, singleMultipleValidationMap).then((data) => {
            counterCustomerChargeVM.onHideLoading();
        });
    }, [counterCustomerChargeVM, searchCriteria, singleMultipleValidationMap]);

    const handleCancel = useCallback(async () => {
        setIsShowSingBlMsg(false);
        setIsShowMultipleBlMsg(false);
        counterCustomerChargeVM.onShowLoading();
        await counterCustomerChargeVM.handleSingBlForSearch(searchCriteria).then((data) => {
            counterCustomerChargeVM.onHideLoading();
        });
    }, [counterCustomerChargeVM, searchCriteria]);

    return (
        <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <HeaderTitle>{COUNTER_CUSTOMER_CHARGE_CONSTANT.TITLE}</HeaderTitle>
                        <SidebarActionCross>
                            {isShowSerachClose && <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={counterCustomerChargeVM.onSerachPanlClose} />}
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>

            <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'visible' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <div className='im-charge-data-search-right-panel-title'>SEARCH CRITERIA</div>
                        <CounterCustomerChargeSearchForm />
                    </div>
                </div>
            </div>
            <SidebarActionBar>
                <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
                <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(counterCustomerChargeState.searchCriteria)} />
            </SidebarActionBar>
            {singleMultipleValidationMap["SingleOrMultiple"] as unknown as string === "Single" && <CommonMessageModal headerText={""}
                contentText={returnCntrNo + " charges found in B/L " + returnBlNo + " . Do you want to search with B/L " + returnBlNo + "?"}
                visible={isShowSingBlMsg}
                footerButtons={[
                    { label: 'Yes', theme: 'Primary', onClick: handleSingBlMsg },
                    { label: 'No', theme: 'Secondary', onClick: handleCancel },
                ]}
            />}
            {singleMultipleValidationMap["SingleOrMultiple"] as unknown as string === "Multiple" && <CommonMessageModal headerText={""}
                contentText={returnCntrNo + " charges found in B/L " + returnBlNo + ", would you like to copy charges to B/L " + (singleMultipleValidationMap["BillOfLadingINCCCM"] as unknown as string) + " in CCCM screen?"}
                visible={isShowMultipleBlMsg}
                footerButtons={[
                    { label: 'Yes', theme: 'Primary', onClick: handleMultipleBlMsg },
                    { label: 'No', theme: 'Secondary', onClick: handleCancel },
                ]}
            />}
        </div>
    );
}

export default CounterCustomerChargeSearchPanel;